// © Microsoft Corporation. All rights reserved.

import { AppType, CrsV3Endpoint, OneServiceFlightPrefix, PeregrineFlightPrefix } from "@msnews/experiences-constants";

/**
 * Specific flights for app types to allow Git configs
 */
export const appTypeExpectedFlightMap: Map<string, string> = new Map([
    [AppType.Edge, "prg-gitcfgs-edge"],
    [AppType.Finance, "prg-gitcfgs-fi"],
    [AppType.SuperApp, "prg-gitcfgs-t"],
    [AppType.Traffic, "prg-gitcfgs-traf"],
    [AppType.Weather, "prg-gitcfgs-wea"]
]);

/**
 * Goes through the array of flights that is given and will filter out any flight that doesn't start with
 * prg-. It then sorts them and returns a new string[].
 * @param flights Array of flights that we will filter through and return only the peregrine
 * @returns specific flights
 */
export function getPeregrineFlights(flights: string[]): string[] {
    if (!flights || flights.length === 0) {
        return flights;
    }

    const cleanedUpFlights = [];
    flights.forEach((item) => {
        const lowercaseItem = item.toLocaleLowerCase().trim();
        // Including specific 1s- prefix to bypass flightID regards OneService's bing.news backfill. Will fill bug to track/remove this.
        if (lowercaseItem.startsWith(PeregrineFlightPrefix) || lowercaseItem.startsWith(OneServiceFlightPrefix)) {
            cleanedUpFlights.push(lowercaseItem);
        }
    });

    return cleanedUpFlights.sort();
}

/**
 * Check if Git configs should be used
 * @param {string} version - CRS version. Example: v2 or v3 (case sensitive)
 * @param {string[]} flights Array of flights. Example: ["testFlight", "prg-abcd"]
 * @param {string} configIndexDocId. Example: cms/api/amp/experienceConfigIndex/BBUsYQa
 * @param {string} apptype. Example: edgeChromium
 * @returns true if git configs are supported
 */
export function shouldUseGitConfigs(version: string, flights: string[], configIndexDocId: string, apptype: string): boolean {
    // If app type is not defined or empty, do not use Git configs.
    // For local dev scenarion of individual experiences running in standalone, app type is blank.
    // Also app type is MUST param in Git configs, if app type is not present, we do not want to attempt Git configs too in prod.
    // If version is not present or it is not v3, do not support Git configs
    if (!apptype || !version || version !== CrsV3Endpoint) {
        return false;
    }

    // Return true if this does not have configIndexDocId
    return !configIndexDocId
        // Return true if this is a Git configs treatment flight
        || containsGitConfigsFlight(apptype, flights);
}

/**
 * Return true if Git configs treatment flight is the expected one for the current apptype
 * @param apptype Current apptype
 * @param flights array of flights active for current page instance
 */
function containsGitConfigsFlight(apptype: string, flights: string[]): boolean {
    const expectedGitFlight = appTypeExpectedFlightMap.get(apptype);

    if (expectedGitFlight) {
        const prgFlights = getPeregrineFlights(flights);

        return !!(prgFlights && prgFlights.length && prgFlights.includes(expectedGitFlight));
    }

    return false;
}